"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function TheAfterhoursPulse() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="476" id="the-afterhours-pulse" className="relative w-full bg-background text-foreground overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[60vh] lg:min-h-[92vh]">
        {}
        <div ref={contentRef} className={`w-full lg:w-[55%] flex items-center order-2 lg:order-1 px-6 py-16 sm:px-10 lg:px-16 xl:pl-[120px] xl:pr-16 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="w-full max-w-xl">
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary" style={{
              color: "var(--primary)"
            }}>
                Live from the Underground
              </span>
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[88px] xl:text-[110px] uppercase">
              <span className="block">Church of over&nbsp;</span>
              <span className="block italic relative text-primary">indulgence<svg className="absolute left-0 -bottom-2 w-[60%] h-3" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none" aria-hidden="true" data-ast-id="f:/src/pages/Sanctuary/sections/TheAfterhoursPulse.tsx|sig:ik33im" data-ast-start="2143" data-ast-end="2432"><path d="M2 8 Q 50 2, 100 6 T 198 4" stroke="var(--chart-2)" stroke-width="2" stroke-linecap="round" fill="none" data-ast-id="f:/src/pages/Sanctuary/sections/TheAfterhoursPulse.tsx|sig:8ui13s" data-ast-start="2296" data-ast-end="2409"></path></svg></span>
            </h1>

            {}
            <p className="font-default mt-10 text-base sm:text-lg lg:text-[19px] leading-relaxed max-w-[480px]" style={{
            color: "var(--muted-foreground)"
          }}>
              Step into the digital sanctuary of the Church of Over Indulgence. 
              Experience high-fidelity event archives and live streams through 
              immersive VR eyewear. The ritual begins now.
            </p>

            {}
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 h-12 text-sm font-mono uppercase tracking-widest shadow-lg">
                <Link to="/VaultPage">
                  Enter The Vault
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Link to="/SanctuaryPage" className="group inline-flex items-center gap-2 text-foreground text-sm font-mono uppercase tracking-widest">
                <span className="relative">
                  The VR Experience
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-0" />
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-primary origin-right scale-x-0 transition-transform duration-500 group-hover:scale-x-100 group-hover:origin-left" />
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <div className="mt-16 hidden md:flex items-center gap-4 opacity-60">
              <svg className="w-32 h-6" viewBox="0 0 140 24" fill="none" aria-hidden="true">
                <path d="M2 12 Q 30 12, 50 8 T 90 14 T 138 10" stroke="var(--primary)" strokeWidth="1" strokeLinecap="round" fill="none" />
                <ellipse cx="22" cy="10" rx="4" ry="2" stroke="var(--primary)" strokeWidth="1" fill="none" transform="rotate(-20 22 10)" />
                <ellipse cx="58" cy="11" rx="4" ry="2" stroke="var(--primary)" strokeWidth="1" fill="none" transform="rotate(15 58 11)" />
                <ellipse cx="95" cy="13" rx="4" ry="2" stroke="var(--primary)" strokeWidth="1" fill="none" transform="rotate(-10 95 13)" />
                <ellipse cx="125" cy="10" rx="4" ry="2" stroke="var(--primary)" strokeWidth="1" fill="none" transform="rotate(20 125 10)" />
              </svg>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{
              color: "var(--muted-foreground)"
            }}>
                Sound · Vision · Ritual
              </span>
            </div>
          </div>
        </div>

        {}
        <div ref={imageRef} className={`relative w-full lg:w-[45%] order-1 lg:order-2 h-[60vh] lg:h-auto lg:min-h-[92vh] overflow-hidden transition-opacity duration-1000 ${imageVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="absolute inset-0 will-change-transform" style={{
          transform: `translateY(${scrollY * 0.15}px)`
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/nightclub_strobe_atmosphere.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/nightclub_strobe_atmosphere.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/nightclub_strobe_atmosphere.webp-wvc-sizes" alt="Vivid stage lighting and dark surfaces in a futuristic nightclub setting" className="w-full h-full object-cover grayscale-[20%] contrast-125" loading="eager" decoding="async" />
          </div>

          {}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(to right, hsla(240, 18%, 5%, 0.8) 0%, hsla(240, 18%, 5%, 0) 25%, hsla(240, 18%, 5%, 0) 75%, hsla(240, 18%, 5%, 0.4) 100%)"
        }} aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(to bottom, hsla(240, 18%, 5%, 0.4) 0%, hsla(240, 18%, 5%, 0) 20%, hsla(240, 18%, 5%, 0) 80%, hsla(240, 18%, 5%, 0.6) 100%)"
        }} aria-hidden="true" />

          {}
          <svg className="absolute top-1/4 left-1/2 -translate-x-1/2 w-32 h-48 pointer-events-none opacity-40" viewBox="0 0 100 150" fill="none" aria-hidden="true">
            <path d="M30 140 Q 25 110, 35 90 Q 45 70, 35 50 Q 25 30, 35 10" stroke="hsla(182, 100%, 75%, 0.6)" strokeWidth="1.5" strokeLinecap="round" fill="none">
              <animate attributeName="d" values="M30 140 Q 25 110, 35 90 Q 45 70, 35 50 Q 25 30, 35 10;
                        M30 140 Q 35 110, 25 90 Q 35 70, 45 50 Q 35 30, 25 10;
                        M30 140 Q 25 110, 35 90 Q 45 70, 35 50 Q 25 30, 35 10" dur="6s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;0.8;0" dur="6s" repeatCount="indefinite" />
            </path>
            <path d="M60 140 Q 65 110, 55 90 Q 45 70, 55 50 Q 65 30, 55 10" stroke="hsla(313, 100%, 65%, 0.5)" strokeWidth="1.5" strokeLinecap="round" fill="none">
              <animate attributeName="d" values="M60 140 Q 65 110, 55 90 Q 45 70, 55 50 Q 65 30, 55 10;
                        M60 140 Q 55 110, 65 90 Q 55 70, 45 50 Q 55 30, 65 10;
                        M60 140 Q 65 110, 55 90 Q 45 70, 55 50 Q 65 30, 55 10" dur="7s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;0.7;0" dur="7s" begin="1s" repeatCount="indefinite" />
            </path>
          </svg>

          {}
          <div className="absolute bottom-8 right-8 lg:bottom-10 lg:right-10 flex items-center gap-3 backdrop-blur-md bg-background/40 px-4 py-3 rounded-md border border-primary/20 shadow-lg">
            <span className="font-default font-bold text-2xl text-primary leading-none">8K</span>
            <div className="flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground leading-tight">
                Immersive
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground leading-tight">
                Resolution
              </span>
            </div>
          </div>

          {}
          <div className="absolute top-8 left-8 lg:top-10 lg:left-10 hidden sm:flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-md bg-destructive animate-pulse" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground">
              Signal Status: Live Stream Active
            </span>
          </div>
        </div>
      </div>
    </section>;
}